// Similarity — "who does this player's real profile most resemble," across the whole
// database, ported from js/renderers-similarity.js via the 04b bridge.
//   04j-b (this): the module core — the base player's radar (+ a click-to-overlay candidate
//     polygon) as the shared radar-chart.ts island, the season/template selects + axis editor in
//     main.js's strip, the ranked candidate list (useSortableRows + up/down quality arrows), the
//     overlay legend (chip + ✕ + hover), and row drag. No filter bar, no report text yet (04j-c).
//
// needsPlayer:true — main.js's createModule owns the strip + the base-player search (its 'change'
// re-renders us with the new playerName). We ADD the season/template/edit-axes controls via
// <SubControlsSlot>. NOT mergeable: the "overlay candidate" is internal module state
// (mod._similarityOverlayCandidate), not a module merge — so no dispatcher.
//
// Reuse-heavy: the chart is the same radar-chart.ts island (multi-entry updateRadarData for base
// 'a' + candidate 'b') over radar-view's axes/scaling; the editor is RadarAxisEditor; the score
// is the pure similarity-view.ts layer via useSimilarityCandidates. The only new UI here is the
// ranked list + the quality arrows.
//
// ?live=1 (Track 2 demo push, the last widget): the two steps (`useSimilaritySeason` /
// `useSimilarityValues`) read her league's Stats file instead of the snapshot — her league, her
// current season, the league's built seasons in the picker, movers left out; the defaults and
// why are in `live/similarity.ts`. Any player in the DB (registered `livePlayers`), as the Radar.

import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainSimilarity } from "./explain.js";
import { chipIsOn } from "../engines/stats-filter-kit.js";
import { isLeagueCleared, isLeagueEmpty, useLeagueChip } from "../hooks/useLeagueChip.js";
import { positionRowKey, whyTwice } from "../live/group-players.js";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { noDataMessage } from "./no-data.js";
import type { ProtoPlayerMetric } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { SubControlsSlot } from "./SubControlsSlot.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { RadarAxisEditor, RadarEditAxesButton } from "./RadarAxisEditor.js";
import { FilterChipBar } from "./FilterChipBar.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import {
  axisValuesMap,
  metricFraction,
  isLowerBetter,
  buildMetricCatalog,
  polygonPoints,
  humanizePositionTemplate,
  TEMPLATE_ORDER,
  type RadarAxisWithRange,
} from "./radar-view.js";
import {
  CHART_SKELETON_HTML,
  ensureChartFrame,
  updateRadarData,
  teardownRadarChart,
  type RadarEntry,
} from "./radar-chart.js";
import { useRadarAxes, useRadarCategories } from "../hooks/useRadarAxes.js";
import { useSimilaritySeason, useSimilarityValues, type SimilarityOverlay } from "../hooks/useSimilarity.js";
import {
  qualityBucket,
  ageFromDob,
  compareTwoPlayers,
  metricRangesForAxes,
  type SimilarityResult,
  type QualityBucket,
} from "./similarity-view.js";
import { buildDifferences, buildAgeSentence, buildMinutesCaveat } from "./similarity-report.js";
import { useSortableRows } from "./useSortableRows.js";
import { type SortColumn } from "./sortable-rows.js";
import { snapshotPlayersOrNone, type RawPlayerRecord } from "../data/snapshot.js";
import { getPlayerShortName, getPlayerInitialName, getPlayerLastName } from "../data/player-utils.js";
import type { ActiveFilters } from "../engines/stats-filter-kit.js";
import { setStickyPlayer } from "../canvas/sticky-player.js";
import { playerIdOf, type PlayerId } from "../data/players.js";
import { startPlayerTokenDrag, startPlayerTokenDragOnMove } from "../canvas/menu-drag.js";
import { LIVE } from "../live/flag.js";
import { pickedShortName } from "../live/picked-players.js";
import { noteRowPlayer } from "../live/player-gender.js";
import { dropLeagueChip } from "../live/comparison.js";

// "Attacking Midfielder" is the one label long enough to force the Pos column wide, so it gets a
// scoped abbreviation (renderers-similarity.js:488) — everything else falls through unchanged.
const SHORT_POSITION_LABELS: Record<string, string> = { attacking_midfielder: "Att Mid" };
function shortPositionLabel(template: string | null | undefined): string {
  const t = template ?? "";
  return SHORT_POSITION_LABELS[t] || humanizePositionTemplate(t);
}

// A small hand-authored inline triangle (renderers-similarity.js:509) — green up for an upgrade,
// red down for a downgrade, nothing for a lateral/unknown.
function QualityArrow({ bucket }: { bucket: QualityBucket }) {
  if (bucket !== "upgrade" && bucket !== "downgrade") return null;
  const up = bucket === "upgrade";
  return (
    <svg className="similarity-trend-icon" viewBox="0 0 12 8" width="10" height="7" aria-hidden="true">
      <polygon points={up ? "2,7 6,1 10,7" : "2,1 6,7 10,1"} fill={up ? "#2ecc71" : "#e2586b"} />
    </svg>
  );
}
function qualityTooltip(bucket: QualityBucket, candName: string, baseName: string): string {
  if (bucket === "upgrade") return `${candName} looks like a likely upgrade — ahead of ${baseName} overall on these axes.`;
  if (bucket === "downgrade") return `${candName} looks like a likely downgrade — behind ${baseName} overall on these axes.`;
  if (bucket === "lateral") return `${candName} looks roughly comparable to ${baseName} overall — a lateral match, not a clear upgrade or downgrade.`;
  return "";
}

// Fixed columns (stable identity — they don't depend on any runtime selection). Player sorts by
// surname (name-like label); Pos/Club as text, Age/Similarity numeric. Matches the legacy header.
const SIM_COLUMNS: SortColumn<SimilarityResult>[] = [
  { label: "Player", get: (r) => getPlayerShortName(r.candidate) },
  { label: "Pos", get: (r) => shortPositionLabel(r.candidate.positionTemplate) },
  { label: "Club", get: (r) => r.candidate.team || "—" },
  { label: "Age", get: (r) => ageFromDob(r.candidate.dob) ?? "—" },
  { label: "Similarity", get: (r) => r.similarityPct },
];

/** `_similarityOverlayCandidate`: the compared player, by id (ids everywhere). */
type OverlayCandidate = SimilarityOverlay;

/** A list row's React key: her name on the snapshot (as always); live, her id + position (a
 *  Position chip can list her twice, and two players can share a name). */
function rowKey(p: RawPlayerRecord): string | undefined {
  return LIVE ? `${playerIdOf(p)}:${p.positionTemplate ?? ""}` : p.name;
}

export function SimilarityModule({ mod, body, playerId, playerName }: ProtoModuleProps) {
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  const [editorOpen, setEditorOpen] = useState(false);
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const svgWrapRef = useRef<HTMLDivElement>(null);

  // The base player: the module's own picker, falling back to the first snapshot player. Live: any
  // player in the DB by id (`playerName` is null for one the snapshot lacks), as the Radar.
  const players = snapshotPlayersOrNone();
  const name = playerName && players[playerName] ? playerName : LIVE && playerId !== null ? "" : (Object.keys(players)[0] ?? "");
  const snapshotBase = players[name] ?? null;
  // Who the base player is, by id — what this module stores.
  const baseId = snapshotBase ? playerIdOf(snapshotBase) : LIVE ? playerId : null;

  // Season/template/overlay/customAxes belong to the current base player; a different player resets
  // them (a chosen season/overlay is meaningless for someone else). renderers-similarity.js:590.
  const seasonOwner = getModState<PlayerId>(mod, "_similaritySeasonPlayer");
  const sameOwner = seasonOwner === baseId;
  const requestedSeason = sameOwner ? getModState<string>(mod, "_similaritySeason") : undefined;
  const storedTemplate = sameOwner ? getModState<string>(mod, "_selectedRadarTemplate") : undefined;
  const customAxes = sameOwner ? (getModState<string[]>(mod, "_radarCustomAxes") ?? null) : null;
  const overlay = sameOwner ? (getModState<OverlayCandidate>(mod, "_similarityOverlayCandidate") ?? null) : null;

  // Step 1 — her season (+ the league's built seasons, live) and the template a fresh tile opens on.
  const sim = useSimilaritySeason(name, baseId, requestedSeason);
  const baseSeason = sim.season;
  // Live: none known (no minutes in this file) → no template until one is picked, so a guess is
  // never kept as her choice (as the live Radar).
  const template = LIVE ? storedTemplate || sim.defaultTemplate : (storedTemplate ?? sim.defaultTemplate);

  const categories = useRadarCategories();
  const catalog = useMemo(() => buildMetricCatalog(categories), [categories]);
  const axes = useRadarAxes(template, customAxes);
  const axisKeys = useMemo(() => axes.map((a) => a.key), [axes]);

  // The module's own compare-against filter state — the same mutable object the chip-bar edits in
  // place (mod._similarityFilters, default {} = the whole population). Seeded once, stable ref, NOT
  // reset on a base-player change (SIMILARITY_RANGE_FILTERS are absolute, categoricals are explicit).
  const [activeFilters] = useState<ActiveFilters>(() => {
    const existing = getModState<ActiveFilters>(mod, "_similarityFilters");
    // Live: the group is already her league (Q-010), so a League chip from a snapshot-saved
    // dashboard is left out (as Comparison's) — it would match nobody for a man.
    if (existing) return LIVE ? dropLeagueChip(existing) : existing;
    const fresh: ActiveFilters = {};
    setModState(mod, "_similarityFilters", fresh);
    return fresh;
  });
  const [filterVersion, setFilterVersion] = useState(0);
  const bumpFilters = useCallback(() => setFilterVersion((v) => v + 1), []);
  const activeSnapshot = useMemo<ActiveFilters>(() => ({ ...activeFilters }), [activeFilters, filterVersion]);
  // Live: the League chip (useLeagueChip — quiet default = her league): which leagues the similar
  // players come from.
  const { onChipsChange, quietKeys } = useLeagueChip(
    mod,
    activeFilters,
    sim.defaultLeague,
    filterVersion,
    bumpFilters,
    "_similarityLeagueQuiet",
  );

  // Step 2 — her record, the ranked list, each spoke's scale and the overlaid player.
  const vals = useSimilarityValues(
    sim,
    template,
    axes,
    axisKeys,
    activeSnapshot,
    overlay,
    isLeagueCleared(mod, activeFilters, "_similarityLeagueQuiet"),
  );
  const base = vals.base;
  const results = vals.results;
  const chartAxes = useMemo<RadarAxisWithRange[]>(
    () => axes.map((a) => ({ ...a, ...(vals.ranges.get(a.key) ?? { min: 0, max: 0 }) })),
    [axes, vals.ranges],
  );

  const { rows, headerProps, rankProps, containerRef } = useSortableRows(results, SIM_COLUMNS);
  // Live: a player listed once per position says why on hover (Q-014). Empty on the snapshot site.
  const twice = useMemo(
    () =>
      whyTwice(
        rows.map((r) => ({
          playerId: (r.candidate as { playerId?: number }).playerId ?? -1,
          shortName: getPlayerShortName(r.candidate),
          competition: r.candidate.competition ?? null,
          positionMinutes: (r.candidate.stats as Record<string, number | null> | undefined)?.minutes ?? null,
        })),
      ),
    [rows],
  );

  const baseValuesMap = useMemo(() => (base ? axisValuesMap(base, baseSeason, chartAxes) : {}), [base, baseSeason, chartAxes]);
  const baseHasAnyAxisData = axisKeys.some((k) => baseValuesMap[k] !== undefined);
  const overlayPlayer = vals.overlay?.record ?? null;
  // The overlay's season as shown (live: the season of the file she was read from).
  const overlaySeason = vals.overlay?.season ?? "";

  // Base entry ('a') + optional candidate entry ('b') — the same multi-entry chart RadarComparison
  // draws. fractions[i] null for a missing axis (never a fabricated point). renderers-similarity.js:704.
  const chartEntries = useMemo<RadarEntry[]>(() => {
    if (!base) return [];
    const baseEntry: RadarEntry = {
      colorLetter: "a",
      fractions: chartAxes.map((a) => metricFraction(baseValuesMap[a.key], { min: a.min, max: a.max }, isLowerBetter(a.key))),
      values: chartAxes.map((a) => (baseValuesMap[a.key] === undefined ? null : baseValuesMap[a.key])),
      tooltipPrefix: `${getPlayerLastName(base)} ${humanizeSeasonCode(baseSeason)}`,
    };
    if (!overlayPlayer || !overlay) return [baseEntry];
    const cvm = axisValuesMap(overlayPlayer, overlaySeason, chartAxes);
    const candEntry: RadarEntry = {
      colorLetter: "b",
      fractions: chartAxes.map((a) => metricFraction(cvm[a.key], { min: a.min, max: a.max }, isLowerBetter(a.key))),
      values: chartAxes.map((a) => (cvm[a.key] === undefined ? null : cvm[a.key])),
      tooltipPrefix: `${getPlayerLastName(overlayPlayer)} ${humanizeSeasonCode(overlaySeason)}`,
    };
    return [baseEntry, candEntry];
  }, [base, baseValuesMap, chartAxes, baseSeason, overlayPlayer, overlay, overlaySeason]);

  // Mirror season/template/owner onto the element (saveState/undo/preset capture them); clear the
  // overlay when the base player changes. renderers-similarity.js:590.
  useEffect(() => {
    if (baseId === null) return;
    const owner = getModState<PlayerId>(mod, "_similaritySeasonPlayer");
    if (owner !== baseId) setModState(mod, "_similarityOverlayCandidate", null);
    setModState(mod, "_similaritySeasonPlayer", baseId);
    setModState(mod, "_similaritySeason", baseSeason);
    setModState(mod, "_selectedRadarTemplate", template);
  }, [mod, baseId, baseSeason, template]);

  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);

  // The chart island: build the skeleton once, tear down the d3 chart on unmount.
  useEffect(() => {
    const wrap = svgWrapRef.current;
    if (!wrap) return;
    wrap.innerHTML = CHART_SKELETON_HTML;
    const svg = wrap.querySelector<SVGSVGElement>(".radar-svg");
    return () => {
      if (svg) teardownRadarChart(svg);
      wrap.innerHTML = "";
    };
  }, []);

  // Draw base (+ candidate) polygons when the axes/entries change. No teardown in cleanup (it would
  // interrupt the update transition each re-render).
  useEffect(() => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    if (!svg || !chartAxes.length || !chartEntries.length) return;
    ensureChartFrame(svg, chartAxes);
    updateRadarData(svg, chartAxes, chartEntries);
  }, [chartAxes, chartEntries]);

  const onSeasonChange = (next: string) => {
    setModState(mod, "_similaritySeason", next);
    setModState(mod, "_similaritySeasonPlayer", baseId);
    forceRender();
  };
  const onTemplateChange = (next: string) => {
    setModState(mod, "_selectedRadarTemplate", next);
    setModState(mod, "_similaritySeasonPlayer", baseId);
    // A customization is scoped to the axis set it was built against (renderers-similarity.js:610).
    setModState(mod, "_radarCustomAxes", null);
    forceRender();
  };

  const toggleOverlay = (candidate: RawPlayerRecord) => {
    const candidateId = playerIdOf(candidate);
    if (overlay && overlay.id === candidateId) {
      setModState(mod, "_similarityOverlayCandidate", null);
    } else {
      const next: OverlayCandidate = { id: candidateId, season: candidate.currentSeason ?? baseSeason };
      // Live, a row from a Position chip is her minutes at that position: the overlay shows those.
      if (LIVE) next.position = chipIsOn(activeSnapshot.position) ? (candidate.positionTemplate ?? null) : null;
      setModState(mod, "_similarityOverlayCandidate", next);
    }
    forceRender();
  };
  const clearOverlay = () => {
    setModState(mod, "_similarityOverlayCandidate", null);
    forceRender();
  };

  // A ranked row is BOTH a click target (toggle the overlay) and a drag source — exactly the legacy
  // (renderers-similarity.js:758/777): the drag only fires on real pointer movement past the shared
  // 3px threshold, so a plain click still reaches onClick.
  const onRowDrag = (e: ReactPointerEvent<HTMLDivElement>, candidate: RawPlayerRecord) => {
    if (window.innerWidth <= 768) return;
    e.preventDefault();
    e.stopPropagation();
    const candidateId = playerIdOf(candidate);
    const begin = () => {
      // Live: she may be no snapshot player — note her (name, team, her league's gender) so the drop
      // can open a module on her and a live Stats / Radar tile takes her (T2-14b, as Filter's rows).
      if (LIVE) noteRowPlayer({ id: candidateId, name: candidate.name ?? "", team: candidate.team ?? null }, sim.womens, candidate.positionTemplate);
      setStickyPlayer(candidateId);
    };
    // Live: the drag (and its ghost) starts only once the pointer moves, so a click just toggles
    // the overlay (Ben, 2026-10-16). The demo keeps the drag starting on press.
    if (LIVE) {
      startPlayerTokenDragOnMove(e.nativeEvent, begin);
      return;
    }
    begin();
    startPlayerTokenDrag(e.nativeEvent);
  };

  // Legend → chart hover (renderers-radar.js wireRadarLegendHover, reused): a hovered chip's player
  // shows as an individual radar (their banded fill via the shared clip) while the other dims.
  const withSvg = (fn: (svg: SVGSVGElement) => void) => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    if (svg) fn(svg);
  };
  const onLegendEnter = (i: number) =>
    withSvg((svg) => {
      const polyG = svg.querySelector<SVGGElement>(".radar-polygons");
      const bandedFillG = svg.querySelector<SVGGElement>(".radar-banded-fill");
      const clipPolygon = svg.querySelector<SVGPolygonElement>(".radar-clip-polygon");
      const entry = chartEntries[i];
      if (!polyG || !bandedFillG || !clipPolygon || !entry) return;
      const target = polyG.querySelector(`.radar-polygon.marker-${entry.colorLetter}`);
      Array.from(polyG.children).forEach((poly) => {
        poly.classList.toggle("compare-highlighted", poly === target);
        poly.classList.toggle("compare-dimmed", poly !== target);
      });
      clipPolygon.setAttribute("points", polygonPoints(chartAxes, entry.fractions));
      polyG.parentNode?.insertBefore(bandedFillG, polyG.nextSibling);
    });
  const onLegendLeave = () =>
    withSvg((svg) => {
      const polyG = svg.querySelector<SVGGElement>(".radar-polygons");
      const bandedFillG = svg.querySelector<SVGGElement>(".radar-banded-fill");
      const clipPolygon = svg.querySelector<SVGPolygonElement>(".radar-clip-polygon");
      if (!polyG || !bandedFillG || !clipPolygon) return;
      Array.from(polyG.children).forEach((poly) => poly.classList.remove("compare-highlighted", "compare-dimmed"));
      clipPolygon.setAttribute("points", "");
      polyG.parentNode?.insertBefore(bandedFillG, polyG);
    });

  if (!LIVE && (!base || !name)) {
    return <div className="stats-empty">No data.</div>;
  }

  // Live: her name from the snapshot, else the file, else her pick (a player with no row here).
  const baseName = snapshotBase ? getPlayerShortName(snapshotBase) : base ? getPlayerShortName(base) : pickedShortName(baseId);
  const seasonItems: StyledSelectItem[] = sim.seasonCodes.map((c) => ({ value: c, label: humanizeSeasonCode(c) }));
  const templateItems: StyledSelectItem[] = TEMPLATE_ORDER.map((t) => ({ value: t, label: humanizePositionTemplate(t) }));
  const hasData = !!base && baseHasAnyAxisData && chartAxes.length > 0;

  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainSimilarity({
            name: baseName,
            season: humanizeSeasonCode(baseSeason),
            template: humanizePositionTemplate(template),
            filters: activeSnapshot,
            noGroup: sim.noGroup,
            leagueEmpty: isLeagueEmpty(mod, activeFilters, "_similarityLeagueQuiet"),
            overlay: overlayPlayer ? getPlayerShortName(overlayPlayer) : null,
          })}
        />
      )}
      {/* Base player's own season, the radar-type (template) select, then the edit-axes button. */}
      <SubControlsSlot mod={mod}>
        <StyledSelect items={seasonItems} value={baseSeason} onChange={onSeasonChange} wrapClass="season-select-wrap" extraClass="season-select" />
        <StyledSelect items={templateItems} value={template} onChange={onTemplateChange} wrapClass="template-select-wrap" extraClass="template-select" />
        <RadarEditAxesButton open={editorOpen} btnRef={editBtnRef} onToggle={() => setEditorOpen((o) => !o)} />
      </SubControlsSlot>

      {editorOpen && editBtnRef.current && (
        <RadarAxisEditor
          anchor={editBtnRef.current}
          mod={mod}
          template={template}
          categories={categories}
          catalog={catalog}
          onChange={forceRender}
          onClose={() => setEditorOpen(false)}
        />
      )}

      <div className="similarity-wrap">
        {sim.noGroup !== null ? (
          <div className="stats-empty">{sim.noGroup}.</div>
        ) : !hasData || !base ? (
          <div className="stats-empty">{noDataMessage("radar", baseName, baseSeason)}</div>
        ) : (
          <>
            {/* Legend only in overlay mode (base chip 'a' + candidate chip 'b'), like Radar's own
                single-player view showing no name label. Both chips hover-highlight their polygon. */}
            <div className="compare-legend" style={overlayPlayer ? undefined : { display: "none" }}>
              {overlayPlayer && overlay && (
                <>
                  <span className="compare-chip compare-chip-a" onMouseEnter={() => onLegendEnter(0)} onMouseLeave={onLegendLeave}>
                    <span className="compare-dot" />
                    {getPlayerInitialName(base)} <span className="compare-chip-season">({humanizeSeasonCode(baseSeason)})</span>
                  </span>
                  <span className="compare-chip compare-chip-b" onMouseEnter={() => onLegendEnter(1)} onMouseLeave={onLegendLeave}>
                    <span className="compare-dot" />
                    {getPlayerInitialName(overlayPlayer)} <span className="compare-chip-season">({humanizeSeasonCode(overlaySeason)})</span>
                    <span className="compare-remove" onClick={clearOverlay}>
                      ✕
                    </span>
                  </span>
                </>
              )}
            </div>

            <div className="radar-svg-wrap" ref={svgWrapRef} />

            {/* Compare-against chip-bar (its own full-width section below the chart) — the shared
                React FilterChipBar over the absolute SIMILARITY_RANGE_FILTERS. An edit mutates
                _similarityFilters in place + bumps the version → a fresh clone into
                useSimilarityCandidates (renderers-similarity.js:746). */}
            <div className="filter-bar">
              <FilterChipBar
                active={activeFilters}
                refPlayer={sim.chips.refPlayer}
                options={sim.chips.options}
                rangeFilters={sim.chips.rangeFilters}
                addMenuKeys={sim.chips.addMenuKeys}
                newChipValue={sim.chips.newChipValue}
                onChange={onChipsChange}
                quietKeys={quietKeys}
                optionInfo={sim.chips.optionInfo}
              />
            </div>
            {/* ✕ on the League chip: no similar players until a league is picked. */}
            {isLeagueEmpty(mod, activeFilters, "_similarityLeagueQuiet") && <div className="stats-empty">Pick a league</div>}

            <div className="similarity-list">
              <div className="data-wrapper" ref={containerRef}>
                <div className="data-row header">
                  <div className="col-rank" onClick={rankProps.onClick}>
                    #
                  </div>
                  <div className={`col-team similarity-col-player ${headerProps(0).className}`.trim()} onClick={headerProps(0).onClick}>
                    Player
                  </div>
                  <div className={`col-num nowrap-text similarity-col-pos ${headerProps(1).className}`.trim()} onClick={headerProps(1).onClick}>
                    Pos
                  </div>
                  <div className={`col-team similarity-col-club ${headerProps(2).className}`.trim()} onClick={headerProps(2).onClick}>
                    Club
                  </div>
                  <div className={`col-num similarity-col-age ${headerProps(3).className}`.trim()} onClick={headerProps(3).onClick}>
                    Age
                  </div>
                  <div className={`col-num similarity-score-col ${headerProps(4).className}`.trim()} onClick={headerProps(4).onClick}>
                    Similarity
                  </div>
                </div>
                {rows.map((r, i) => {
                  const pl = r.candidate;
                  const bucket = qualityBucket(r.netAdvantage, r.sharedAxisCount);
                  const age = ageFromDob(pl.dob);
                  const isActive = overlay?.id === playerIdOf(pl);
                  return (
                    <div
                      key={rowKey(pl)}
                      data-sort-key={rowKey(pl)}
                      className={`data-row similarity-row-clickable${isActive ? " highlighted" : ""}`}
                      onClick={() => toggleOverlay(pl)}
                      onPointerDown={(e) => onRowDrag(e, pl)}
                      data-tooltip={twice.get(
                        positionRowKey({ playerId: (pl as { playerId?: number }).playerId ?? -1, competition: pl.competition ?? null }),
                      )}
                    >
                      <div className="col-rank">{i + 1}</div>
                      <div className="col-team similarity-col-player">{getPlayerShortName(pl)}</div>
                      <div className="col-num nowrap-text similarity-col-pos">{shortPositionLabel(pl.positionTemplate)}</div>
                      <div className="col-team similarity-col-club">{pl.team || "—"}</div>
                      <div className="col-num similarity-col-age" data-val={age ?? ""}>
                        {age ?? "—"}
                      </div>
                      <div
                        className="col-num similarity-score-col"
                        data-val={r.similarityPct}
                        title={qualityTooltip(bucket, getPlayerShortName(pl), baseName) || undefined}
                      >
                        {r.similarityPct.toFixed(1)}%
                        <QualityArrow bucket={bucket} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="similarity-report">
              {overlayPlayer && overlay ? (
                <SimilarityReport
                  base={base}
                  baseSeason={baseSeason}
                  candidate={overlayPlayer}
                  candSeason={overlaySeason}
                  axisKeys={axisKeys}
                  catalog={catalog}
                  population={vals.rangePopulation}
                />
              ) : (
                <div className="stats-empty">Click a player above to see how they compare.</div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// The templated report (04j-c) — Notable differences / Age / Minutes caveat, from 04j-a's pure
// builders. renderers-similarity.js:425. candSeason is the overlay's season (a dragged-in legend
// chip can carry an older season), so it's recomputed here rather than reusing the list's
// always-currentSeason result.
function SimilarityReport({
  base,
  baseSeason,
  candidate,
  candSeason,
  axisKeys,
  catalog,
  population,
}: {
  base: RawPlayerRecord;
  baseSeason: string;
  candidate: RawPlayerRecord;
  candSeason: string;
  axisKeys: readonly string[];
  catalog: Record<string, ProtoPlayerMetric>;
  /** Who each metric's range comes from (the snapshot's players, or live her league's file). */
  population: Record<string, RawPlayerRecord>;
}) {
  const players = population;
  const rangeByKey = useMemo(() => metricRangesForAxes(players, baseSeason, axisKeys), [players, baseSeason, axisKeys]);
  const cmpResult = useMemo(
    () => compareTwoPlayers(base, baseSeason, candidate, candSeason, axisKeys, rangeByKey),
    [base, baseSeason, candidate, candSeason, axisKeys, rangeByKey],
  );
  const diffs = useMemo(
    () => buildDifferences({ base, baseSeason, candidate, candSeason, axisKeys, catalog, rangeByKey }),
    [base, baseSeason, candidate, candSeason, axisKeys, catalog, rangeByKey],
  );
  const ageSentence = buildAgeSentence(base, baseSeason, candidate, cmpResult);
  const minutesCaveat = buildMinutesCaveat(base, baseSeason, candidate);
  const baseName = getPlayerShortName(base);
  const candName = getPlayerShortName(candidate);

  return (
    <>
      <div className="similarity-report-header">
        {baseName} vs {candName}
      </div>
      <div className="similarity-report-section">
        <div className="similarity-report-section-label">Notable differences</div>
        {diffs.length ? (
          <ul className="similarity-diff-list">
            {diffs.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        ) : (
          <div className="similarity-report-empty">No large statistical differences on these axes — a strong match.</div>
        )}
      </div>
      <div className="similarity-report-section">
        <div className="similarity-report-section-label">Age</div>
        <div className="similarity-caveat">{ageSentence || "—"}</div>
      </div>
      {minutesCaveat && (
        <div className="similarity-report-section">
          <div className="similarity-caveat similarity-caveat-warning">{minutesCaveat}</div>
        </div>
      )}
    </>
  );
}
